import type { VenueSceneContext, VenueSceneHandle } from "../../types";
import { useVenueStore } from "../../venueStore";
import { asCasinoState } from "../casinoView";
import { Bag } from "./kit";
import { buildStatic } from "./staticBuild";
import { DrinksView, SlotsView, TablesView, WheelView } from "./views";

/**
 * Townsy Casino's 3D (ADR-0016) on top of the generic room: the fixed casino floor plus the parts that follow the
 * server's shared state (cards, chips, reels, the wheel, drinks). It only displays; every outcome is the server's.
 * Everything created here is disposed when the visitor leaves the room.
 */
export function buildCasino3d(ctx: VenueSceneContext): VenueSceneHandle {
  const { scene } = ctx;
  const bag = new Bag();
  const fixed = buildStatic(scene, bag);
  const tables = new TablesView(scene, bag);
  const slots = new SlotsView(scene, bag);
  const wheel = new WheelView(scene, bag, fixed.layoutCells);
  const drinks = new DrinksView(scene, bag);
  ctx.addShadowCasters([...fixed.casters, ...wheel.casters()]);
  let disposed = false;

  const apply = (raw: unknown) => {
    const s = asCasinoState(raw);
    if (!s) return;
    const at = performance.now();
    tables.update(s);
    slots.update(s, at);
    wheel.update(s, at);
    drinks.update(s);
  };
  // The room's state can arrive before its 3D is built: start from what the client already holds.
  const held = useVenueStore.getState();
  if (held.venue === "casino" && held.shared) apply(held.shared);

  return {
    update(state) {
      if (!disposed) apply(state);
    },
    frame(_dtMs, nowMs) {
      if (disposed) return;
      tables.frame(nowMs);
      slots.frame(nowMs);
      wheel.frame(nowMs);
      drinks.frame();
    },
    dispose() {
      if (disposed) return;
      disposed = true;
      drinks.dispose();
      bag.dispose();
    },
  };
}
